/**
 * flickity-swatch-perf.js
 *
 * WHAT THIS WORKS AROUND
 * ----------------------
 * _updateImages() in theme.min.js re-attaches and then detaches every
 * variant-linked gallery slide on each swatch change, one
 * flickityInstance.append()/remove() call at a time. Each call triggers a full
 * Flickity layout pass:
 *
 *   insert() -> _sizeCells()        (forces a synchronous layout read per cell)
 *            -> _cellAddedRemoved() -> cellChange() -> _positionCells,
 *                                       setGallerySize, positionSliderAtSelected
 *   remove() -> _cellAddedRemoved() -> cellChange() -> (same)
 *
 * Measured, unthrottled desktop, Sep 2026:
 *   Hometown Yarn    206 media, 203 removes/click : 2715ms -> 98ms  (27.8x)
 *   Wool-Ease T&Q    241 media, 236 removes/click : 3320ms -> 93ms  (35.8x)
 * Removes and appends per click are IDENTICAL with and without this patch —
 * it changes when layout is computed, never what gets moved.
 *
 * WHAT THIS DOES
 * --------------
 * Stubs cellChange and _sizeCells for the duration of the theme's synchronous
 * variant:changed handlers, then runs one resize() afterwards. Every DOM
 * mutation still happens in the same order, so the state _updateImages depends
 * on (its data-media-position renumbering and the selectCell that follows) is
 * untouched.
 *
 * KILL SWITCH
 * -----------
 * Add ?fsp=off to any URL to disable for that page load. Use this to rule the
 * patch in or out of a bug report without deploying.
 *
 * FAILURE BEHAVIOUR
 * -----------------
 * - Install is wrapped in try/catch: if anything throws, methods are restored
 *   immediately and the event proceeds with stock (slow) behaviour.
 * - A watchdog restores any leaked stub at the start of the next event, so bad
 *   state cannot persist past one swatch click.
 * - If the theme's handler throws mid-burst, the already-queued flush still
 *   restores and repositions.
 * - If this file fails entirely, the page reverts to stock behaviour: slow
 *   swatch clicks, nothing broken.
 * - Blast radius is one Flickity instance on product pages. It cannot affect
 *   cart, checkout, forms, navigation, or any other section.
 *
 * WHY THE isActive CHECKS
 * -----------------------
 * The slideshow runs with watchCSS:true, and theme.scss disables Flickity in
 * the stacked-gallery layout via `.Product__Gallery--stack .Product__Slideshow
 * ::after { content: '' }`. Flickity's resize() is a no-op when isActive is
 * false, but cellChange/_sizeCells are NOT guarded and still run. Patching
 * while inactive would suppress the layout work with no flush to recover it,
 * so we skip entirely. isActive is re-checked at flush time because watchCSS
 * can flip it on a breakpoint change inside our timeout window.
 *
 * We do NOT re-select after resize(): Flickity's resize() already ends with
 * selectCell(selectedElements[0], false, true), which re-selects by element.
 *
 * FRAGILITY — READ BEFORE UPGRADING
 * ---------------------------------
 * Depends on Flickity internals (cellChange, _sizeCells, resize, isActive) and
 * on _updateImages being synchronous. Verified against these builds:
 *   Both stores, as of Sept 30 2026 (CA theme parity update):
 *     theme.min.js md5 1c97521f94b1905cd14f3eb52c6db6c3
 *     libs.min.js  md5 27e13a5f7d91bf8e0f7227b8b5c315be
 *   CA previously ran theme.min.js 51589c906352fdee72ba22ecd0e17e08, a
 *   different bundle whose _updateImages was byte-identical to this one.
 * A Flickity upgrade or a theme update
 * that rewrites _updateImages can break or silently obsolete this.
 * If the gallery misbehaves after any upgrade, remove this file first.
 *
 * SUPERSEDED BY: batching the loops in _updateImages so they pass an array to
 * remove()/append() instead of calling once per element. See PATCHES.md.
 */
(function () {
  var SLIDESHOW = '.Product__Slideshow';
  var busy = false;

  if (window.location.search.indexOf('fsp=off') !== -1) {
    console.info('[flickity-swatch-perf] disabled via ?fsp=off');
    return;
  }

  function instance() {
    var el = document.querySelector(SLIDESHOW);
    if (!el || !window.Flickity || typeof Flickity.data !== 'function') return null;
    return Flickity.data(el) || null;
  }

  // Undo any stub left behind by an earlier run that did not complete.
  function restore(f, cellChange, sizeCells) {
    try {
      if (cellChange) f.cellChange = cellChange;
      if (sizeCells) f._sizeCells = sizeCells;
    } catch (e) { /* nothing further we can safely do */ }
  }

  function watchdog(f) {
    var proto = Object.getPrototypeOf(f);
    if (!proto) return;
    var leaked = false;
    if (f.cellChange !== proto.cellChange && f.cellChange && f.cellChange.__fspStub) {
      f.cellChange = proto.cellChange;
      leaked = true;
    }
    if (f._sizeCells !== proto._sizeCells && f._sizeCells && f._sizeCells.__fspStub) {
      f._sizeCells = proto._sizeCells;
      leaked = true;
    }
    if (leaked) {
      busy = false;
      console.warn('[flickity-swatch-perf] leaked stub restored by watchdog');
    }
  }

  document.addEventListener('variant:changed', function () {
    var f = instance();
    if (!f) return;

    // A run already in flight will restore via its own flush. Running the
    // watchdog here would mistake that run's stubs for a leak and undo them
    // mid-burst, defeating the busy guard and queueing a second resize.
    if (busy) return;

    try { watchdog(f); } catch (e) { /* non-fatal */ }

    if (!f.isActive) return;                       // stacked layout: leave stock
    if (typeof f.cellChange !== 'function' || typeof f._sizeCells !== 'function') return;
    if (typeof f.resize !== 'function') return;

    var realCellChange = f.cellChange;
    var realSizeCells = f._sizeCells;

    var stubCell = function () {};
    var stubSize = function () {};
    stubCell.__fspStub = true;
    stubSize.__fspStub = true;

    try {
      busy = true;
      f.cellChange = stubCell;
      f._sizeCells = stubSize;

      // Restores on the next task, after the theme's synchronous handlers run.
      setTimeout(function () {
        try {
          restore(f, realCellChange, realSizeCells);
          if (f.isActive) f.resize();
        } catch (err) {
          restore(f, realCellChange, realSizeCells);
          console.error('[flickity-swatch-perf] flush failed, carousel restored:', err);
        } finally {
          busy = false;
        }
      }, 0);
    } catch (err) {
      // Anything went wrong while installing: undo and let the theme run stock.
      restore(f, realCellChange, realSizeCells);
      busy = false;
      console.error('[flickity-swatch-perf] install failed, running unpatched:', err);
    }
  }, true); // capture: must run before the theme's delegate handlers on the section
})();
